import { PlaybookApiError } from "./errors.js";

/** Statuses that are an ANSWER from the server, not an outage — never retried.
 *  400/422 judge the REQUEST itself (a malformed id, a bad parameter), so the
 *  identical retry can only get the identical answer. Retrying them held a
 *  page on its loading skeleton for three back-offs before it could say what
 *  was wrong (an invalid TTI id stuck on "Loading target…"). */
const TERMINAL_READ_STATUSES = new Set([400, 401, 403, 404, 422]);

/** Default retry budget: three retries after the original call (four attempts). */
const MAX_RETRIES = 3;

/**
 * TanStack Query `retry` predicate for module READ queries.
 *
 * Two failure modes, two behaviours:
 *
 * - **400 / 401 / 403 / 404 / 422** — the server has answered. Retrying cannot change the
 *   result, it just delays the UI settling and multiplies the request. Fail fast.
 * - **anything else** (5xx, network, timeout) — transient. Retry, so one blip
 *   does not leave a query permanently stuck at `data === undefined`.
 *
 * That second half matters more than it looks: a query with `retry: false` whose
 * consumer gates rendering on `data !== undefined` latches closed for the life of
 * the mount, because the shell's QueryClient also sets
 * `refetchOnWindowFocus: false` — so nothing ever refetches it. That combination
 * silently hid four Hoshin session panels (PB-498) and is the reason this lives in
 * one shared place instead of being re-derived per feature package.
 *
 * Shared here rather than duplicated per feature: root CLAUDE.md puts shared
 * frontend code in `packages/`, never copied across app or feature packages.
 */
export function retryTransientRead(
  failureCount: number,
  error: Error,
): boolean {
  return retryTransientReadStatus(
    failureCount,
    error instanceof PlaybookApiError ? error.status : undefined,
  );
}

/**
 * The same policy, keyed on a STATUS rather than on `PlaybookApiError`.
 *
 * `retryTransientRead` can only fast-fail an error it can recognise, and the
 * `instanceof` check silently does nothing for a client that throws its own
 * error type — `@playbookai/notification-client` throws
 * `NotificationClientError extends Error`, so every 401/403/404 from it fell
 * through to the transient branch and was retried three times. On a query that
 * polls, that multiplies a single expired session into four requests per tick.
 *
 * So callers outside the `PlaybookApiError` family pass their own status here
 * instead of re-deriving the terminal set. `undefined` means "no status to
 * judge" — a network or timeout failure — which is transient and retried.
 */
export function retryTransientReadStatus(
  failureCount: number,
  status: number | undefined,
): boolean {
  if (status !== undefined && TERMINAL_READ_STATUSES.has(status)) {
    return false;
  }
  return failureCount < MAX_RETRIES;
}
